import { useState, type ReactNode } from "react";

export type FormField = {
  name: string;
  label: string;
  type?: "text" | "number" | "email" | "select" | "textarea";
  options?: ReadonlyArray<{ value: string; label: string }>;
  required?: boolean;
  placeholder?: string;
  defaultValue?: string | number;
  hint?: string;
};

export type FormProps = {
  title?: string;
  description?: string;
  fields: ReadonlyArray<FormField>;
  /** Submit label. If `confirmBeforeSubmit` is set, label becomes "Review then send". */
  submitLabel?: string;
  /**
   * If set, intercepts submit with a confirm step (Aiko's "asks before
   * irreversible" rule, contract:1bed8068). The confirm body shows a
   * summary the user reviews.
   */
  confirmBeforeSubmit?: { title: string; body: ReactNode };
  onSubmit: (values: Record<string, string>) => Promise<void> | void;
};

export function Form({
  title,
  description,
  fields,
  submitLabel,
  confirmBeforeSubmit,
  onSubmit,
}: FormProps) {
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.name, String(f.defaultValue ?? "")])),
  );
  const [stage, setStage] = useState<"edit" | "confirm" | "busy">("edit");

  function update(name: string, v: string) {
    setValues((s) => ({ ...s, [name]: v }));
  }

  async function reallySubmit() {
    setStage("busy");
    try {
      await onSubmit(values);
    } finally {
      setStage("edit");
    }
  }

  if (stage === "confirm" && confirmBeforeSubmit) {
    return (
      <div className="rounded-xl border border-warn/40 bg-warn/10 p-5 space-y-3">
        <h3 className="font-display text-lg text-ink">{confirmBeforeSubmit.title}</h3>
        <div className="text-sm text-ink-soft">{confirmBeforeSubmit.body}</div>
        <p className="text-sm text-ink">This step is irreversible. Are you sure?</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setStage("edit")}
            className="rounded-md border border-line bg-canvas-2 px-4 py-2 text-sm"
          >
            Back
          </button>
          <button
            type="button"
            onClick={reallySubmit}
            className="rounded-md bg-warn text-canvas px-4 py-2 text-sm font-medium"
          >
            Yes, send it
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (confirmBeforeSubmit) setStage("confirm");
        else void reallySubmit();
      }}
      className="rounded-xl border border-line bg-canvas-2 p-5 space-y-4"
    >
      {title && <h3 className="font-display text-lg text-ink">{title}</h3>}
      {description && <p className="text-sm text-ink-soft">{description}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {fields.map((f) => (
          <label key={f.name} className="space-y-1">
            <span className="text-sm text-ink-soft">
              {f.label}
              {f.required && <span className="text-bad ml-0.5">*</span>}
            </span>
            {f.type === "textarea" ? (
              <textarea
                value={values[f.name]}
                onChange={(e) => update(f.name, e.target.value)}
                required={f.required}
                placeholder={f.placeholder}
                rows={3}
                className="w-full rounded-md border border-line bg-canvas px-3 py-2 text-sm"
              />
            ) : f.type === "select" ? (
              <select
                value={values[f.name]}
                onChange={(e) => update(f.name, e.target.value)}
                required={f.required}
                className="w-full rounded-md border border-line bg-canvas px-3 py-2 text-sm"
              >
                <option value="">Pick one</option>
                {f.options?.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={f.type ?? "text"}
                value={values[f.name]}
                onChange={(e) => update(f.name, e.target.value)}
                required={f.required}
                placeholder={f.placeholder}
                className="w-full rounded-md border border-line bg-canvas px-3 py-2 text-sm"
              />
            )}
            {f.hint && <p className="text-xs text-ink-soft">{f.hint}</p>}
          </label>
        ))}
      </div>

      <button
        type="submit"
        disabled={stage === "busy"}
        className="rounded-md bg-accent text-canvas px-4 py-2 text-sm font-medium hover:brightness-110 transition disabled:opacity-60"
      >
        {stage === "busy"
          ? "Working..."
          : confirmBeforeSubmit
            ? "Review then send"
            : (submitLabel ?? "Submit")}
      </button>
    </form>
  );
}
